import { CHIP_INLINE } from './chrome.js';
import { ObservationBlock } from './ObservationBlock.js';
import { ConfirmInline } from '../chrome/ConfirmInline.js';
import { ModifierLadder } from '../orders/ModifierLadder.js';
import { DetailBlock } from '../rails/DetailBlock.js';
import {
  creditAt,
  identityOptions,
  modelById,
  nextStageOf,
  signed,
  trackStateOf,
  type MwModelWire,
  type MwTrackWire,
} from '../model/mw.js';
import { mwTrackChip, mwTrackRows } from '../model/selection.js';
import type { LadderRow } from '../model/ladder.js';
import { useConsoleStore } from '../state/store.js';
import { BODY, DATA, DATA_LABEL, HEADLINE } from '../../theme/type.js';

/**
 * The selected launch event (design 881–995), in the console's right rail.
 *
 * The design gives the MW view its own two rails. Ours already has two, and the
 * right one is the selection rail — which is what this panel is: a track is a
 * *selection*, resolved against whatever snapshot is current, surviving the
 * wholesale swap on every stage arrival. That is the ledger's second row
 * collecting: `DetailBlock` takes `DetailRow[]` and needed no change to draw a
 * subject it was written before.
 *
 * ## The credit ladder, and why it is `ModifierLadder`
 *
 * The ledger's fourth row, collected. The ladder takes `label · detail · mod`
 * rows and knows nothing about success chances — its `mod` column is a *string*,
 * which is exactly what lets it print `+4`, `0` and `DATA HELD NOW` where the
 * order builder prints percentages. It is drawn open rather than behind a control
 * because the price of waiting is not a detail of this decision; it *is* the
 * decision.
 *
 * ## Two components, because the actions may never scroll away
 *
 * `TrackPanel` is the reading — identity, credit, observations — and it scrolls.
 * `TrackActions` is IDENTIFY, the two dispositions and the confirm, and the rail
 * pins it to the bottom, outside the scroll. The visual pass is what found this:
 * with four stages of observations in a 344 px rail and the order builder holding
 * the bottom, the release control and the confirm it opens were both below the
 * fold — and a confirm the operator cannot see is worse than no confirm, because
 * as far as they can tell the console has stopped responding.
 *
 * ## What is not here
 *
 * No sensor block (Q13) and no HOLD (Q14). Waiting is the absence of an action,
 * and a button for it would turn "I am not ready to call this" into a thing the
 * operator has to remember to do.
 */

export interface TrackPanelProps {
  readonly track: MwTrackWire;
  readonly catalogue: readonly MwModelWire[];
  readonly serverNowMs: number;
  readonly onAction: (
    eventId: string,
    action: 'IDENTIFY' | 'RELEASE' | 'DISMISS',
    identity?: string,
  ) => void;
  readonly onOpenTable: () => void;
  /**
   * Whether the centre pane is drawing the reading, so this panel should not.
   *
   * With the map switched off (`MwMap.MW_MAP_ENABLED`) the board has a whole
   * empty column and the rail has 203 px of scroll under the pinned actions and
   * the order builder — measured against 826 px of content at stage 4, so three
   * quarters of the reading was below the fold at the moment it mattered most.
   * The pane takes it. The rail keeps the identity, the state and the actions,
   * which is what a *selection* rail is for.
   *
   * False when the operator is on another tab with a track still selected: the
   * pane is drawing something else entirely then, and the rail is the only home
   * the reading has.
   */
  readonly readingInPane?: boolean;
}

/**
 * What the seat has picked, falling back to what the server already holds.
 *
 * The draft and `track.workingIdentity` are deliberately separate keys: the
 * second is the result of an IDENTIFY the server accepted, and collapsing them
 * would make picking a name in the box look like asserting it.
 */
function identityOf(track: MwTrackWire, drafts: Readonly<Record<string, string>>): string {
  return drafts[track.eventId] ?? track.workingIdentity ?? '';
}

export function TrackPanel(props: TrackPanelProps): JSX.Element {
  const track = props.track;
  const drafts = useConsoleStore((state) => state.mwIdentityDraft);

  const state = trackStateOf(track);
  const identity = identityOf(track, drafts);
  const chosen = modelById(props.catalogue, identity === '' ? null : identity);

  return (
    <>
      <DetailBlock
        id={track.eventId}
        idColour={state === 'DISMISSED' ? 'var(--text-2)' : 'var(--red)'}
        chip={mwTrackChip(track)}
        name={chosen?.displayName ?? 'UNIDENTIFIED'}
        kindLabel={track.briefed ? 'LAUNCH EVENT · BRIEFED' : 'LAUNCH EVENT'}
        rows={mwTrackRows(track)}
      />

      {props.readingInPane !== true && (
        <TrackReading track={track} serverNowMs={props.serverNowMs} />
      )}
    </>
  );
}

/**
 * The reading: what the constellation has said, and what a call is worth now.
 *
 * Its own component because it has two homes. In the centre pane it is the
 * board's right half, with the height a four-stage reveal actually needs; in the
 * right rail it sits under the detail block, which is where it goes when the
 * pane is drawing another tab. Same component either way — the alternative was
 * two copies of the one part of this seat nobody can afford to have drift.
 */
export function TrackReading(props: {
  readonly track: MwTrackWire;
  readonly serverNowMs: number;
}): JSX.Element {
  const track = props.track;
  const fullSignature = useConsoleStore((state) => state.mwFullSignature);
  const setFullSignature = useConsoleStore((state) => state.setMwFullSignature);
  const next = nextStageOf(track, props.serverNowMs);

  return (
    <>
      <div
        data-testid="mw-credit-ladder"
        style={{ flex: 'none', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}
      >
        <ModifierLadder
          title="WARNING CREDIT"
          rows={creditRows(track, next?.clock ?? null)}
          footnote={FOOTNOTE}
          testId="mw-ladder"
          resting
        />
      </div>

      <ObservationBlock track={track} />

      {/* The full signature is the whole reveal at once, without its stage
          headings — what an operator wants when they are comparing against the
          table rather than watching data arrive. */}
      {track.observations.length > 0 && (
        <button
          type="button"
          data-testid="mw-full-signature"
          aria-pressed={fullSignature}
          onClick={() => setFullSignature(!fullSignature)}
          style={TOGGLE}
        >
          {fullSignature ? 'HIDE FULL SIGNATURE' : 'FULL SIGNATURE'}
        </button>
      )}
      {fullSignature && <FullSignature track={track} />}
    </>
  );
}

export function TrackActions(props: TrackPanelProps): JSX.Element {
  const track = props.track;
  const drafts = useConsoleStore((state) => state.mwIdentityDraft);
  const setDraft = useConsoleStore((state) => state.setMwIdentityDraft);
  const confirm = useConsoleStore((state) => state.confirm);
  const requestConfirm = useConsoleStore((state) => state.requestConfirm);
  const clearConfirm = useConsoleStore((state) => state.clearConfirm);

  const state = trackStateOf(track);
  const decided = track.release !== null;
  const identity = identityOf(track, drafts);
  const stage = track.revealedThroughStage;

  // A track with no data is not actionable (M4-D6): the API refuses it, and
  // offering the buttons anyway would teach the seat that "release everything at
  // t=0" is a strategy the console was willing to send.
  const actionable = !decided && stage > 0;

  const pending =
    confirm !== null && (confirm.kind === 'mw-release' || confirm.kind === 'mw-dismiss')
      ? confirm
      : null;
  const asking = pending !== null && pending.eventId === track.eventId ? pending : null;

  return (
    <div
      data-testid="mw-actions"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-2)',
        borderTop: '1px solid var(--border-strong)',
      }}
    >
      {!decided && (
        <div
          data-testid="mw-identify"
          style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...DATA_LABEL, flex: 1, color: 'var(--text-2)' }}>IDENTITY</span>
            <button
              type="button"
              data-testid="mw-open-table"
              onClick={props.onOpenTable}
              style={LINK}
            >
              RECOGNITION TABLE
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select
              data-testid="mw-identity"
              value={identity}
              onChange={(event) => setDraft(track.eventId, event.target.value)}
              style={SELECT}
            >
              <option value="">—</option>
              {identityOptions(props.catalogue).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id} · {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="mw-assert"
              disabled={!actionable || identity === ''}
              onClick={() => props.onAction(track.eventId, 'IDENTIFY', identity)}
              style={{
                ...ACTION,
                flex: 'none',
                width: 92,
                opacity: actionable && identity !== '' ? 1 : 0.45,
              }}
            >
              IDENTIFY
            </button>
          </div>
        </div>
      )}

      {actionable && asking === null && (
        <div
          data-testid="mw-dispositions"
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 6,
            padding: '0 12px 10px',
          }}
        >
          <button
            type="button"
            data-testid="mw-release"
            disabled={identity === ''}
            onClick={() => requestConfirm({ kind: 'mw-release', eventId: track.eventId, stage })}
            style={{
              ...ACTION,
              flex: 1,
              background: 'var(--red)',
              border: '1px solid var(--red)',
              color: 'var(--red-on)',
              opacity: identity === '' ? 0.45 : 1,
            }}
          >
            RELEASE
          </button>
          <button
            type="button"
            data-testid="mw-dismiss"
            onClick={() => requestConfirm({ kind: 'mw-dismiss', eventId: track.eventId, stage })}
            style={{ ...ACTION, flex: 1 }}
          >
            DISMISS
          </button>
        </div>
      )}

      {/* The case `ConfirmInline` was shaped against. Its detail line carries the
          stage credit — the whole price of acting now rather than waiting — and
          the credit is the one from the moment the question was asked, not from
          whatever stage has landed since. */}
      {asking !== null && (
        <ConfirmInline
          testId="mw-confirm"
          prompt={
            asking.kind === 'mw-release'
              ? 'Release a warning on ' + track.eventId + ' as ' + identity + '?'
              : 'Dismiss ' + track.eventId + ' as benign?'
          }
          detail={'STAGE ' + String(asking.stage) + ' · CREDIT ' + signed(creditAt(asking.stage))}
          confirmLabel={asking.kind === 'mw-release' ? 'RELEASE' : 'DISMISS'}
          // `KEEP` is the default, and it is the queue's word — there is
          // something to keep when the question is whether to cancel a drafted
          // order. There is nothing to keep here. Backing out of a disposition
          // is holding the track, which is also the word this panel already
          // uses for the decision it deliberately does not build a button for.
          cancelLabel="HOLD"
          onCancel={clearConfirm}
          onConfirm={() => {
            clearConfirm();
            if (asking.kind === 'mw-release') {
              props.onAction(track.eventId, 'RELEASE', identity);
            } else {
              // DISMISS ignores `identity` (`zMwActionPayload`), so none is sent.
              props.onAction(track.eventId, 'DISMISS');
            }
          }}
        />
      )}

      {decided && track.release !== null && (
        <div
          data-testid="mw-terminal"
          data-disposition={track.release.disposition}
          style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '10px 12px' }}
        >
          <span
            style={{
              ...HEADLINE,
              fontSize: 13,
              color: state === 'RELEASED' ? 'var(--red-text)' : 'var(--text-2)',
            }}
          >
            {track.release.disposition === 'RELEASE' ? 'WARNING RELEASED' : 'DISMISSED AS BENIGN'}
          </span>
          {/* One short line that says what changed, and the only place the banked
              credit appears. RELEASE and DISMISS are terminal (§8.3): there is no
              second disposition here, and there is no control to undo one. */}
          <span style={{ ...BODY, fontSize: 12, color: 'var(--text-muted)' }}>
            {track.workingIdentity ?? track.release.identity} at stage {track.release.stage}
            {track.briefed ? '.' : ' · ' + signed(creditAt(track.release.stage)) + ' credit.'}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * What a decision now is worth, against what it would have been worth earlier.
 *
 * Every stage is listed, so the seat can see the shape of the price rather than
 * only today's number — the whole mechanic is that credit falls as certainty
 * rises, and one number cannot say that. Values come from `MW_STAGE_CREDIT`
 * through `creditAt`; nothing here re-derives them (Q27).
 */
function creditRows(track: MwTrackWire, nextClock: string | null): readonly LadderRow[] {
  const stage = track.revealedThroughStage;
  const released = track.release?.stage ?? null;
  return ([1, 2, 3, 4] as const).map((candidate) => ({
    key: 'STAGE' + String(candidate),
    label: 'STAGE ' + String(candidate),
    detail:
      released === candidate
        ? 'CALLED HERE'
        : candidate === stage && released === null
          ? 'DATA HELD NOW'
          : candidate === stage + 1 && released === null && nextClock !== null
            ? 'DUE IN ' + nextClock
            : '',
    mod: signed(creditAt(candidate)),
    tone:
      released === candidate
        ? 'good'
        : candidate === stage && released === null
          ? 'warn'
          : 'neutral',
  }));
}

const FOOTNOTE = 'STAGE TERM ONLY · CORRECTNESS IS GRADED AT RESOLUTION AGAINST THE LAUNCH';

function FullSignature(props: { readonly track: MwTrackWire }): JSX.Element {
  return (
    <div
      data-testid="mw-signature"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        padding: '4px 14px 10px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {props.track.observations.map((observation) => (
        <div
          key={String(observation.stage) + ':' + observation.field}
          data-testid="mw-signature-row"
          data-dropped={observation.value === null ? 'true' : undefined}
          style={{
            ...DATA,
            display: 'flex',
            gap: 10,
            padding: '3px 0',
            fontWeight: 400,
            color: observation.value === null ? 'var(--warn)' : 'var(--text-2)',
          }}
        >
          <span style={{ flex: 'none', color: 'var(--text-muted)' }}>{observation.field}</span>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'right', wordBreak: 'break-word' }}>
            {observation.value ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

const ACTION = {
  ...HEADLINE,
  height: 28,
  background: 'var(--surface-3)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  fontSize: 12,
  cursor: 'pointer',
} as const;

/** The recognition-table link, on the shared chip so it is one thing in one place. */
const LINK = CHIP_INLINE;

const SELECT = {
  flex: 1,
  minWidth: 0,
  height: 28,
  padding: '0 6px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  fontFamily: 'var(--font-data)',
  fontSize: 12,
} as const;

const TOGGLE = {
  ...DATA_LABEL,
  flex: 'none',
  height: 26,
  margin: '8px 14px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-muted)',
  fontWeight: 400,
  cursor: 'pointer',
} as const;
